import { Plus } from 'lucide-react';

import type { CopyElement } from '../../api';
import { COPY_TYPE_OPTIONS, type TaxonomyOption } from '../../constants/taxonomy';

/**
 * The card under a creative in the detail overlay: what it was cut from, and
 * its copy set the way a layout would set it, each line labelled with its type.
 *
 * One card for every creative that carries copy — a carousel's frame and a
 * single master image — so the two read alike. Where there is no copy and the
 * viewer may edit, the card is the way in: "Add copy" (and "Edit" once there
 * is some) hands over to `onEdit`, which opens wherever that copy is edited.
 */

/** The order copy is set in, as a layout would: what reads first, first. */
const COPY_ORDER = ['headline', 'subheadline', 'offer', 'cta', 'disclaimer'];

const COPY_LABELS = new Map(COPY_TYPE_OPTIONS.map((option) => [option.value, option.label]));

const COPY_STYLE: Record<string, string> = {
  headline: 'text-[16px] font-semibold leading-snug tracking-[-0.01em] text-slate-900',
  subheadline: 'text-[13px] leading-snug text-slate-600',
  offer: 'text-[13px] font-semibold text-[#0014cc]',
  cta: 'text-[12.5px] font-semibold text-slate-800',
  disclaimer: 'text-[10.5px] leading-snug text-slate-400',
};

export function sortCopy(copy: readonly CopyElement[] | null | undefined): CopyElement[] {
  const rank = (type: string) => {
    const index = COPY_ORDER.indexOf(type);
    return index === -1 ? COPY_ORDER.length : index;
  };
  return [...(copy ?? [])].sort((a, b) => rank(a.type) - rank(b.type));
}

/** "Headline, subheadline, offer or disclaimer", from the types on offer. */
function typesInWords(options: readonly TaxonomyOption[]): string {
  const labels = options.map((option, index) =>
    index === 0
      ? option.label
      : option.label.length <= 3
        ? option.label
        : option.label.toLowerCase(),
  );
  return labels.length > 1
    ? `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`
    : (labels[0] ?? '');
}

interface CopyCaptionProps {
  /** Big numeral in the corner, a carousel frame's "02". Left out for a single image. */
  marker?: string;
  /** Codes of the source images it was cut from; none shows a warning chip. */
  sourceCodes: readonly string[];
  filename: string;
  copy: readonly CopyElement[] | null | undefined;
  /** What the caption is of, for its wording: "this frame", "this image". */
  subject: 'frame' | 'image';
  /** The copy types this creative may carry, for the "Add copy" hint. */
  typeOptions?: readonly TaxonomyOption[];
  /** Open wherever this copy is edited. Left out for a viewer who cannot edit. */
  onEdit?: () => void;
}

export function CopyCaption({
  marker,
  sourceCodes,
  filename,
  copy: unsorted,
  subject,
  typeOptions = COPY_TYPE_OPTIONS,
  onEdit,
}: CopyCaptionProps) {
  const copy = sortCopy(unsorted);

  return (
    <div className="relative grid w-full max-w-xl animate-caption-in grid-cols-[auto_1fr] gap-x-5 rounded-lg border border-slate-200 bg-white/95 px-4 py-3 shadow-sm motion-reduce:animate-none">
      {onEdit && copy.length > 0 && (
        <button
          type="button"
          onClick={onEdit}
          className="absolute right-2.5 top-2 rounded px-1.5 py-0.5 text-[11px] font-medium text-[#0014cc] hover:bg-[#0014cc]/[0.07]"
        >
          Edit
        </button>
      )}
      <div className="flex flex-col items-start gap-1.5 border-r border-slate-100 pr-5">
        {marker && (
          <span className="text-[28px] font-light leading-none tabular-nums text-[#0014cc]">
            {marker}
          </span>
        )}
        {sourceCodes.length > 0 ? (
          sourceCodes.map((code) => (
            <span
              key={code}
              className="rounded bg-[#0014cc]/[0.07] px-1.5 py-0.5 text-[10.5px] font-semibold tracking-wide text-[#0014cc]"
            >
              {code}
            </span>
          ))
        ) : (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-amber-700">
            No source image
          </span>
        )}
        <span className="max-w-[9rem] truncate text-[10.5px] text-slate-400" title={filename}>
          {filename}
        </span>
      </div>
      <div className={`flex min-w-0 flex-col justify-center gap-1 ${onEdit ? 'pr-8' : ''}`}>
        {copy.length > 0 ? (
          // Each line labelled with its type, in a column of its own, so
          // "0% APR" reads as the offer at a glance; the line itself keeps
          // the weight it would have in the layout.
          <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1.5">
            {copy.map((row, index) => (
              <div key={index} className="contents">
                <dt className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                  {COPY_LABELS.get(row.type) ?? row.type}
                </dt>
                <dd className={COPY_STYLE[row.type] ?? 'text-[13px] text-slate-700'}>
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : onEdit ? (
          // Where the copy is missing is where to add it: one click opens the
          // editor with a headline row ready to type in.
          <button
            type="button"
            onClick={onEdit}
            className="group flex w-full items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2.5 text-left transition-colors hover:border-[#0014cc]/50 hover:bg-[#0014cc]/[0.04]"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0014cc]/[0.08] text-[#0014cc] transition-colors group-hover:bg-[#0014cc] group-hover:text-white">
              <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
            </span>
            <span className="flex flex-col">
              <span className="text-[12.5px] font-medium text-slate-800">Add copy</span>
              <span className="text-[11px] text-slate-400">
                {typesInWords(typeOptions)} for this {subject}
              </span>
            </span>
          </button>
        ) : (
          <p className="text-[12px] italic text-slate-400">No copy on this {subject}</p>
        )}
      </div>
    </div>
  );
}
